import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { MessageCircle, AlertCircle, Zap } from 'lucide-react';
import { Button } from './ui/button';
import Header from './Header';
import Footer from './Footer';
import FloatingWhatsApp from './FloatingWhatsApp';

const BlogSalvavitaPiove = () => {
  const handleWhatsApp = () => {
    window.open('https://wa.me/393287686356?text=Ciao,%20il%20salvavita%20scatta%20quando%20piove.%20Potete%20intervenire?', '_blank');
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Perché Scatta il Salvavita Quando Piove | Guida SOS Casa Roma</title>
        <meta name="description" content="Salvavita che scatta quando piove? Scopri le cause e cosa fare subito. Elettricista urgente H24 a Roma. Tel: 328 768 6356" />
        <link rel="canonical" href={`${window.location.origin}/blog/salvavita-scatta-quando-piove`} />
      </Helmet>
      
      <div className="min-h-screen bg-gray-50">
        <Header />
        
        <article className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-lg p-8">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Perché Scatta il Salvavita Quando Piove
              </h1>
              
              <div className="prose max-w-none">
                <p className="text-lg text-gray-700 mb-6">
                  Se il salvavita (interruttore differenziale) scatta ogni volta che piove, c'è un problema di infiltrazione d'acqua nell'impianto elettrico. È un segnale serio che richiede attenzione immediata.
                </p>

                <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
                  <div className="flex items-start">
                    <AlertCircle className="h-6 w-6 text-red-500 mr-3 flex-shrink-0 mt-1" />
                    <p className="text-gray-800">
                      <strong>PERICOLO:</strong> Non sottovalutare questo problema. L'acqua nell'impianto elettrico può causare scosse elettriche e incendi. Chiama subito un elettricista.
                    </p>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                  Perché Succede
                </h2>
                <p className="text-gray-700 mb-4">
                  Il salvavita scatta quando rileva una "dispersione di corrente". Quando piove, l'acqua può infiltrarsi e creare contatto tra cavi elettrici e masse metalliche, facendo scattare la protezione.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                  Cause Più Comuni
                </h2>
                
                <div className="space-y-4 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="font-semibold text-gray-900 flex items-center gap-2">
                      <Zap className="h-5 w-5 text-blue-600" />
                      Prese esterne non stagne
                    </p>
                    <p className="text-gray-700 ml-7">Prese da esterno (giardino, balcone) senza protezione IP adeguata. L'acqua entra nella presa.</p>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="font-semibold text-gray-900 flex items-center gap-2">
                      <Zap className="h-5 w-5 text-blue-600" />
                      Cavi del condizionatore
                    </p>
                    <p className="text-gray-700 ml-7">Cavi esterni del clima non sigillati. L'acqua scorre lungo il cavo ed entra in casa.</p>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="font-semibold text-gray-900 flex items-center gap-2">
                      <Zap className="h-5 w-5 text-blue-600" />
                      Infiltrazioni dal tetto
                    </p>
                    <p className="text-gray-700 ml-7">Acqua piovana che infiltra nelle pareti e raggiunge cavi elettrici o scatole di derivazione.</p>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="font-semibold text-gray-900 flex items-center gap-2">
                      <Zap className="h-5 w-5 text-blue-600" />
                      Fori nei muri esterni
                    </p>
                    <p className="text-gray-700 ml-7">Fori per cavi condizionatore o antenne mal sigillati che permettono infiltrazioni d'acqua.</p>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="font-semibold text-gray-900 flex items-center gap-2">
                      <Zap className="h-5 w-5 text-blue-600" />
                      Quadro elettrico umido
                    </p>
                    <p className="text-gray-700 ml-7">Quadro posizionato in zona umida (cantina, garage) che assorbe umidità quando piove.</p>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                  Cosa Fare Immediatamente
                </h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold">!</div>
                    <div>
                      <p className="font-semibold text-gray-900">NON riattaccare subito</p>
                      <p className="text-gray-700">Se continua a scattare, significa che il problema è attivo. Non insistere.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold">1</div>
                    <div>
                      <p className="font-semibold text-gray-900">Stacca tutto dall'esterno</p>
                      <p className="text-gray-700">Scollega prese esterne, condizionatori, luci da giardino. Poi prova a riattaccare il salvavita.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold">2</div>
                    <div>
                      <p className="font-semibold text-gray-900">Individua la zona</p>
                      <p className="text-gray-700">Abbassa un interruttore magnetotermico alla volta per capire quale linea ha il problema.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold">3</div>
                    <div>
                      <p className="font-semibold text-gray-900">Chiama un elettricista</p>
                      <p className="text-gray-700">Serve un professionista per individuare e risolvere l'infiltrazione in sicurezza.</p>
                    </div>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                  Come Risolve un Elettricista Professionista
                </h2>
                <p className="text-gray-700 mb-4">
                  SOS Casa Roma interviene con:
                </p>
                <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                  <li>Megaohmetro per individuare esattamente il punto di dispersione</li>
                  <li>Sostituzione prese esterne con modelli IP65 stagne</li>
                  <li>Sigillatura fori esterni con silicone specifico</li>
                  <li>Rifacimento guaine e protezione cavi esposti</li>
                  <li>Controllo e asciugatura scatole di derivazione</li>
                  <li>Verifica impianto di messa a terra</li>
                </ul>

                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-6">
                  <p className="text-gray-800">
                    <strong>Prevenzione:</strong> Controlla regolarmente che prese esterne, cavi clima e fori nei muri siano ben sigillati, specialmente prima della stagione delle piogge.
                  </p>
                </div>

                <div className="bg-blue-600 rounded-lg p-8 text-center mt-12">
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Salvavita che Scatta Quando Piove? Intervento Urgente
                  </h3>
                  <p className="text-white text-lg mb-6">
                    Elettricisti certificati DM 37/08 con strumentazione per ricerca guasti. Disponibili H24, anche con maltempo. Arriviamo in 30-60 minuti a Roma.
                  </p>
                  <Button
                    onClick={handleWhatsApp}
                    size="lg"
                    className="bg-orange-500 hover:bg-orange-600 text-white text-xl px-10 py-7 rounded-lg shadow-lg transition-all duration-300 hover:scale-105"
                  >
                    <MessageCircle className="mr-3 h-7 w-7" />
                    Chiama SOS Casa Roma su WhatsApp
                  </Button>
                  <p className="text-white mt-4 font-semibold">
                    📞 328 768 6356
                  </p>
                </div>
              </div>
            </div>
          </div>
        </article>

        <Footer />
        <FloatingWhatsApp />
      </div>
    </>
  );
};

export default BlogSalvavitaPiove;
